<ng-container *ngIf="!loading; else isLoading">
  <!-- Error State -->
  <div *ngIf="error" class="row mb--md">
    <div class="column--12">
      <div class="error-container">
        <mat-icon color="warn">error</mat-icon>
        <p class="error-text">{{ error }}</p>
        <button mat-flat-button color="primary" (click)="handleError()">Retry</button>
      </div>
    </div>
  </div>

  <div class="column--2 align-right">
    <form [formGroup]="form">
      <mat-form-field class="w-100">
        <mat-label>Couriers</mat-label>
        <mat-select formControlName="couriers" name="couriers" placeholder="All couriers" multiple>
          <mat-option *ngFor="let option of couriersDropdown" value="{{ option.id }}">{{ option.text }}</mat-option>
        </mat-select>
      </mat-form-field>
    </form>
  </div>

  <!-- Summary Section -->
  <div class="row mb--md">
    <!-- Service Type Summary Card -->
    <div class="column--12">
      <mat-card class="summary-card">
        <mat-card-content class="card p--md">
          <mat-card-title>SERVICE TYPE SUMMARY</mat-card-title>
          <div class="row summary-content">
            <div class="column--3">
              <p>Total Services</p>
              <h2>{{ totalServices | number }}</h2>
            </div>
            <div class="column--3">
              <p>Total Shipments</p>
              <h2>{{ totalShipments | number }}</h2>
            </div>
            <div class="column--3">
              <p>Avg On-Time Rate</p>
              <h2>{{ onTimeRate | number : '1.2-2' }}%</h2>
            </div>
            <div class="column--3">
              <p>Guaranteed Services</p>
              <h2>{{ guaranteedServicesCount | number }}</h2>
            </div>
          </div>
          <div class="row">
            <div class="column--3">
              <p>Non-Guaranteed</p>
              <h2>{{ nonGuaranteedServicesCount | number }}</h2>
            </div>
            <div class="column--3">
              <p>Average Delay</p>
              <h2>{{ averageDelay | number : '1.2-2' }}h</h2>
            </div>

            <!-- Claims metrics (always show if available) -->
            <div class="column--3" *ngIf="hasClaimsData">
              <p>Total Claims</p>
              <h2>{{ totalClaims | number }}</h2>
            </div>
            <div class="column--3" *ngIf="hasClaimsData">
              <p>Claim Rate</p>
              <h2 [ngStyle]="{ color: getClaimRateColor(overallClaimRate) }">
                {{ formatClaimRate(overallClaimRate) }}
                <mat-icon class="trend-icon small" [ngClass]="getClaimsTrendClass(claimRateTrend)">
                  {{ getTrendIcon(claimRateTrend) }}
                </mat-icon>
              </h2>
            </div>
          </div>
          <!-- Show a message if no claims data is available -->
          <div class="row" *ngIf="!hasClaimsData">
            <div class="column--12">
              <p class="no-claims-message">No claims data available for this time period.</p>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Performance & Service Type Analysis Section -->
  <div class="row mb--md">
    <!-- Service Type Volume Card -->
    <div class="column--12 column-md--6">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>SERVICE TYPE VOLUME</mat-card-title>
          <div class="chart-container">
            <ngx-charts-bar-horizontal
              [results]="serviceBreakdown"
              [xAxis]="true"
              [yAxis]="true"
              [scheme]="defaultColorScheme"
              [xAxisTickFormatting]="formatYAxisNumberTicks">
            </ngx-charts-bar-horizontal>
          </div>
          <div class="chart-note">Showing top 10 services by volume</div>
          <button mat-button color="primary" (click)="scrollToTable()">View All Services in Table</button>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Performance by Service Type Card -->
    <div class="column--12 column-md--6">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>PERFORMANCE BY SERVICE TYPE</mat-card-title>
          <div class="chart-container">
            <ngx-charts-bar-horizontal
              [results]="servicePerformanceData"
              [xAxis]="true"
              [yAxis]="true"
              [scheme]="performanceColorScheme"
              [xAxisTickFormatting]="formatYAxisPercentageTicks">
            </ngx-charts-bar-horizontal>
          </div>
          <div class="chart-note">Showing top 10 services by volume</div>
          <button mat-button color="primary" (click)="scrollToTable()">View All Services in Table</button>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Claim Rate by Service Type Card (shown only when claims data is available) -->
  <div class="row mb--md" *ngIf="hasClaimsData">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>CLAIM RATE BY SERVICE TYPE</mat-card-title>
          <div class="chart-container" *ngIf="serviceClaimRateData.length > 0">
            <ngx-charts-bar-horizontal
              [results]="serviceClaimRateData"
              [xAxis]="true"
              [yAxis]="true"
              [scheme]="claimsColorScheme"
              [xAxisTickFormatting]="formatClaimRateTicks">
              <ng-template #tooltipTemplate let-model="model">
                <div class="tooltip-content">
                  <span>{{ model.name }}</span>
                  <br />
                  <span>Claim Rate: {{ (model.value / 100) | percent : '1.2-2' }}</span>
                  <br />
                  <span>Claims: {{ model.extra.totalClaims | number }}</span>
                  <br />
                  <span>Shipments: {{ model.extra.totalShipments | number }}</span>
                </div>
              </ng-template>
          </ngx-charts-bar-horizontal>
          </div>
          <!-- Show message if no claims data for service types -->
          <div class="no-data-message" *ngIf="serviceClaimRateData.length === 0">
            <p>No claim rate data available for service types in this time period.</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Trends Section -->
  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>PERFORMANCE TRENDS BY SERVICE TYPE</mat-card-title>
          <div class="chart-container">
            <ngx-charts-line-chart
              [results]="serviceTrendData"
              [view]="[800, 400]"
              [xAxis]="true"
              [yAxis]="true"
              [legend]="true"
              [showXAxisLabel]="true"
              [showYAxisLabel]="true"
              xAxisLabel="Time Period"
              yAxisLabel="On-Time Percentage (%)"
              [yAxisTickFormatting]="formatYAxisPercentageTicks"
              [scheme]="defaultColorScheme"
              [autoScale]="true"
            >
            </ngx-charts-line-chart>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Claims Trend Analysis (shown only when claims data is available) -->
  <div class="row mb--md" *ngIf="hasClaimsData && serviceClaimsTrendData.length > 0">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>CLAIMS TREND ANALYSIS</mat-card-title>
          <div class="chart-container">
            <ngx-charts-line-chart
              [results]="serviceClaimsTrendData"
              [view]="[800, 400]"
              [xAxis]="true"
              [yAxis]="true"
              [legend]="true"
              [showXAxisLabel]="true"
              [showYAxisLabel]="true"
              xAxisLabel="Time Period"
              yAxisLabel="Claim Rate (%)"
              [yAxisTickFormatting]="formatClaimRateTicks"
              [scheme]="claimsColorScheme"
              [autoScale]="true"
            >
            </ngx-charts-line-chart>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Service Volume Stacked Chart -->
  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>SERVICE VOLUME OVER TIME</mat-card-title>
          <div class="chart-container">
            <ngx-charts-bar-vertical-stacked
              [results]="serviceVolumeData"
              [view]="[800, 400]"
              [scheme]="defaultColorScheme"
              [gradient]="false"
              [xAxis]="true"
              [yAxis]="true"
              [legend]="true"
              [showXAxisLabel]="true"
              [showYAxisLabel]="true"
              xAxisLabel="Time Period"
              yAxisLabel="Shipment Volume"
              [yAxisTickFormatting]="formatYAxisNumberTicks"
              [animations]="true"
            >
            </ngx-charts-bar-vertical-stacked>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Service Comparison Table -->
  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="table-card" id="serviceComparisonTable">
        <mat-card-content class="card p--md">
          <mat-card-title>SERVICE TYPE COMPARISON</mat-card-title>
          <div class="responsive-table">
            <table mat-table [dataSource]="displayedDetailedMetrics" class="mat-elevation-z8">
              <!-- Service Type Column -->
              <ng-container matColumnDef="serviceType">
                <th mat-header-cell *matHeaderCellDef>SERVICE TYPE</th>
                <td mat-cell *matCellDef="let element">{{ element.serviceName }}</td>
              </ng-container>

              <!-- Courier Column -->
              <ng-container matColumnDef="courier">
                <th mat-header-cell *matHeaderCellDef>COURIER</th>
                <td mat-cell *matCellDef="let element">{{ element.courier }}</td>
              </ng-container>

              <!-- Volume Column -->
              <ng-container matColumnDef="volume">
                <th mat-header-cell *matHeaderCellDef>VOLUME</th>
                <td mat-cell *matCellDef="let element">{{ element.volume | number }}</td>
              </ng-container>

              <!-- On-Time % Column -->
              <ng-container matColumnDef="onTimePercent">
                <th mat-header-cell *matHeaderCellDef>ON-TIME %</th>
                <td mat-cell *matCellDef="let element">
                  {{ element.onTimePercent | number : '1.2-2' }}%
                  <mat-icon class="trend-icon small" [ngClass]="getClaimsTrendClass(element.trend)">
                    {{ getTrendIcon(element.trend) }}
                  </mat-icon>
                </td>
              </ng-container>

              <!-- Avg Delay Column -->
              <ng-container matColumnDef="avgDelay">
                <th mat-header-cell *matHeaderCellDef>AVG DELAY</th>
                <td mat-cell *matCellDef="let element">{{ element.avgDelay | number : '1.2-2' }}h</td>
              </ng-container>

              <!-- Is Guaranteed Column -->
              <ng-container matColumnDef="isGuaranteed">
                <th mat-header-cell *matHeaderCellDef>GUARANTEED</th>
                <td mat-cell *matCellDef="let element">{{ element.isGuaranteed }}</td>
              </ng-container>

              <!-- Claims Column -->
              <ng-container matColumnDef="claims">
                <th mat-header-cell *matHeaderCellDef>CLAIMS</th>
                <td mat-cell *matCellDef="let element">{{ element.claims | number }}</td>
              </ng-container>

              <!-- Claim Rate Column -->
              <ng-container matColumnDef="claimRate">
                <th mat-header-cell *matHeaderCellDef>CLAIM RATE</th>
                <td mat-cell *matCellDef="let element" [ngStyle]="{ color: getClaimRateColor(element.claimRate) }">
                  {{ formatClaimRate(element.claimRate) }}
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
          </div>

          <!-- Pagination for table -->
          <mat-paginator
            [length]="detailedMetrics.length"
            [pageSize]="itemsPerPage"
            [pageSizeOptions]="itemsPerPageOptions"
            [pageIndex]="currentPage"
            (page)="pageChanged($event)"
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</ng-container>

<!-- Loading Template -->
<ng-template #isLoading>
  <div class="align-center mt--lg">
    <app-loading></app-loading>
  </div>
</ng-template>
